/**
 * Word Collection type definitions for Flow-State word organization system.
 *
 * Organizes words by engagement state (collected/set_aside) rather than
 * traditional categorization. Tracks discovery provenance, exploration progress,
 * and timeline metadata for informed retrieval and re-engagement.
 */

import type { RhymeCategory } from '../utils/rhyme/types';

// ============================================================================
// STATUS AND CLASSIFICATION TYPES
// ============================================================================

/** Engagement state of a word - collected means actively in play, set_aside means benched */
export type WordStatus = 'collected' | 'set_aside'

/** How a word entered the collection - tracks provenance for context */
export type DiscoveryMethod = 'user_brainstorm' | 'thesaurus_lookup' | 'rhyme_exploration'

/** Rhyme classification - technical categorization of phonetic relationship */
export type RhymeType = 'perfect' | 'family' | 'additive' | 'subtractive' | 'assonance' | 'consonance'

/** Constrained syllable counts - most useful range for songwriting */
export type SyllableCount = 1 | 2 | 3 | 4

// ============================================================================
// DISCOVERY METADATA
// ============================================================================

/**
 * Thesaurus lookup provenance - captures the exploration path.
 * Helps user retrace how they found this word and in what semantic context.
 */
export interface ThesaurusData {
  /** Original word looked up (e.g., "love") */
  source_word: string
  /** The index entry word that led here (e.g., "affection") */
  index_entry: string
  /** Semantic sense/meaning group (e.g., "romantic attachment") */
  sense: string
  /** Category within that sense (e.g., "passionate") */
  category: string
}

/**
 * Rhyme exploration provenance - records which word this was found under
 * and the explorer category at collection time.
 *
 * Keeps collection grouping/label aligned with explorer intent even when
 * the source word is absent from the phonetic dictionary.
 */
export interface RhymeData {
  /** The word that was searched when this word appeared as a result (e.g., "hobo") */
  source_word: string
  /** Explorer category at collection time (perfect, family, etc.) */
  rhyme_type?: RhymeType
  /** Family rhyme subcategory if applicable (voiced_plosive, etc.) */
  subcategory?: string
}

/**
 * Discovery metadata - answers "where did this word come from?"
 * Enables informed filtering and re-engagement based on discovery context.
 */
export interface DiscoveryMetadata {
  method: DiscoveryMethod
  /** Full thesaurus provenance if method='thesaurus_lookup' */
  thesaurus_data?: ThesaurusData
  /** Rhyme search provenance if method='rhyme_exploration' */
  rhyme_data?: RhymeData
}

// ============================================================================
// EXPLORATION STATE
// ============================================================================

/**
 * Exploration progress - tracks what avenues have been investigated.
 * Prevents duplicate work and shows potential next steps.
 *
 * Note: rhyme_count was removed in Scope 07 (flat model cleanup).
 * The frontend no longer manages per-word rhyme collections.
 */
export interface ExplorationState {
  /** Have we fetched rhymes for this word yet? */
  rhymes_explored: boolean
  /** Have we searched thesaurus for this word yet? */
  thesaurus_explored: boolean
}

// ============================================================================
// TIMELINE TRACKING
// ============================================================================

/**
 * Timeline metadata - tracks state transitions and engagement history.
 * Supports intelligent re-surfacing of set-aside words.
 */
export interface Timeline {
  /** When word entered the collection */
  discovered_at: Date
  /** Last time moved to 'collected' or modified while collected */
  last_active_at?: Date
  /** When moved to 'set_aside' (undefined if never set aside) */
  set_aside_at?: Date
  /** Why set aside - helps decide if it's worth reconsidering */
  set_aside_reason?: string
}

// ============================================================================
// PRIMARY ENTITIES
// ============================================================================

/**
 * Word - the primary collection entity.
 *
 * Represents a word the user has collected for their song.
 * Organized by engagement state (collected/set_aside) rather than rigid categories
 * to support fluid creative exploration and retrieval.
 */
export interface Word {
  id: string
  songId: string
  word: string
  status: WordStatus

  discovery: DiscoveryMetadata
  notes?: string
  exploration: ExplorationState
  timeline: Timeline
}

// ============================================================================
// FACTORY INPUT TYPES
// ============================================================================

/**
 * Input props for creating a Word - requires core fields, applies sensible defaults.
 * discovery is optional — defaults to user_brainstorm when not provided,
 * matching the backend's default behavior (Scope 07).
 */
export interface CreateWordProps {
  id: string
  songId: string
  word: string
  status?: WordStatus
  discovery?: DiscoveryMetadata
  notes?: string
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Validate WordStatus literal - ensures value is a valid status enum.
 */
function isWordStatus(value: unknown): value is WordStatus {
  return (
    typeof value === 'string' &&
    (value === 'collected' || value === 'set_aside')
  )
}

/**
 * Validate DiscoveryMethod literal - ensures value is a valid method enum.
 */
function isDiscoveryMethod(value: unknown): value is DiscoveryMethod {
  return (
    typeof value === 'string' &&
    (value === 'user_brainstorm' || value === 'thesaurus_lookup' || value === 'rhyme_exploration')
  )
}

/**
 * Validate ThesaurusData shape - checks all required fields exist and are correct type.
 */
function isThesaurusData(value: unknown): value is ThesaurusData {
  if (typeof value !== 'object' || value === null) return false
  const data = value as Record<string, unknown>
  return (
    typeof data.source_word === 'string' &&
    typeof data.index_entry === 'string' &&
    typeof data.sense === 'string' &&
    typeof data.category === 'string'
  )
}

/**
 * Validate RhymeData shape - checks source_word and optional category fields.
 */
function isRhymeData(value: unknown): value is RhymeData {
  if (typeof value !== 'object' || value === null) return false
  const data = value as Record<string, unknown>
  // source_word is required
  if (typeof data.source_word !== 'string') return false
  // rhyme_type is optional but must be a valid RhymeType if present
  if (data.rhyme_type !== undefined && typeof data.rhyme_type !== 'string') return false
  // subcategory is optional but must be string if present
  if (data.subcategory !== undefined && typeof data.subcategory !== 'string') return false
  return true
}

/**
 * Validate DiscoveryMetadata shape - checks method and optional provenance fields.
 */
function isDiscoveryMetadata(value: unknown): value is DiscoveryMetadata {
  if (typeof value !== 'object' || value === null) return false
  const metadata = value as Record<string, unknown>

  // Method is required and must be valid
  if (!isDiscoveryMethod(metadata.method)) return false

  // thesaurus_data is optional but must be valid if present
  if (metadata.thesaurus_data !== undefined && !isThesaurusData(metadata.thesaurus_data)) {
    return false
  }

  // rhyme_data is optional but must be valid if present
  if (metadata.rhyme_data !== undefined && !isRhymeData(metadata.rhyme_data)) {
    return false
  }

  return true
}

/**
 * Validate ExplorationState shape - checks required boolean fields.
 */
function isExplorationState(value: unknown): value is ExplorationState {
  if (typeof value !== 'object' || value === null) return false
  const state = value as Record<string, unknown>
  return (
    typeof state.rhymes_explored === 'boolean' &&
    typeof state.thesaurus_explored === 'boolean'
  )
}

/**
 * Validate Timeline shape - checks required discovered_at and optional state transition dates.
 */
function isTimeline(value: unknown): value is Timeline {
  if (typeof value !== 'object' || value === null) return false
  const timeline = value as Record<string, unknown>

  // discovered_at is required and must be a Date
  if (!(timeline.discovered_at instanceof Date)) return false

  // Optional fields must be Date or string if present
  if (
    timeline.last_active_at !== undefined &&
    !(timeline.last_active_at instanceof Date) &&
    typeof timeline.last_active_at !== 'string'
  ) {
    return false
  }

  if (
    timeline.set_aside_at !== undefined &&
    !(timeline.set_aside_at instanceof Date) &&
    typeof timeline.set_aside_at !== 'string'
  ) {
    return false
  }

  if (timeline.set_aside_reason !== undefined && typeof timeline.set_aside_reason !== 'string') {
    return false
  }

  return true
}

/**
 * Type guard for Word - validates complete runtime shape.
 *
 * Checks all required fields exist with correct types, including nested
 * discovery, exploration, and timeline objects.
 */
export function isWord(value: unknown): value is Word {
  if (typeof value !== 'object' || value === null) return false
  const word = value as Record<string, unknown>

  // Validate required primitive fields
  if (typeof word.id !== 'string') return false
  if (typeof word.songId !== 'string') return false
  if (typeof word.word !== 'string') return false
  if (!isWordStatus(word.status)) return false

  // Validate nested objects
  if (!isDiscoveryMetadata(word.discovery)) return false
  if (!isExplorationState(word.exploration)) return false
  if (!isTimeline(word.timeline)) return false

  // notes is optional but must be string if present
  if (word.notes !== undefined && typeof word.notes !== 'string') {
    return false
  }

  return true
}

// ============================================================================
// RHYME GROUPING TYPES
// ============================================================================

/**
 * A group of words sharing a phonetic rhyme signature.
 * Singletons (words with no partners or unknown pronunciation) get their own group.
 *
 * Moved here from rhymeGrouping.ts (deleted in scope 13-04) so all three consumers
 * — useRhymeGrouping, WordCollectionSidebar, RhymeGroupCluster — import from one place.
 */
export interface RhymeGroup {
  /** Phonetic key, e.g. "EH_D" for head/said/bread, or "__singleton_love" */
  phoneticKey: string;
  /** All words sharing this phonetic key */
  words: string[];
  /** Best rhyme category for this group; NONE for singletons */
  dominantCategory: RhymeCategory;
  /**
   * Optional word ID of the designated "key word" for this group.
   * Managed at the sidebar level — persisted per-song via localStorage.
   * Undefined means "use the default" (first word in group).
   */
  keyWordId?: string;
}

// ============================================================================
// BACKEND RHYME CATEGORIZATION API TYPES
// ============================================================================

/** Minimal word shape sent to categorization endpoints */
export interface CategorizeWordInput {
  id: string
  word: string
}

/** Per-word result inside a backend rhyme group */
export interface BackendGroupWord {
  id: string
  word: string
  /** Backend category string: "key_word", "perfect", "family_voiced", etc. */
  category: string
}

/** A phonetically-grouped cluster returned by the backend */
export interface BackendRhymeGroup {
  group_id: string
  phonetic_key: string
  key_word_id: string
  words: BackendGroupWord[]
}

/** A word that didn't fit into any phonetic group */
export interface BackendSingleton {
  id: string
  word: string
  phonemes: string[] | null
  reason: string
}

export interface CategorizeCollectionRequest {
  words: CategorizeWordInput[]
  key_word_overrides: Record<string, string>
  song_id?: string
}

export interface CategorizeCollectionResponse {
  groups: BackendRhymeGroup[]
  singletons: BackendSingleton[]
  processing_time_ms: number
}

export interface CategorizeWordsRequest {
  key_word: string
  words: string[]
}

export interface CategorizeWordResult {
  word: string
  phonemes: string[] | null
  anchor: string | null
  category: string
  group_compatible: boolean
}

export interface CategorizeWordsResponse {
  key_word: string
  key_anchor: string | null
  results: CategorizeWordResult[]
}

export interface RecategorizeGroupRequest {
  key_word: { id: string; word: string }
  words: CategorizeWordInput[]
}

export interface RecategorizeWordResult {
  id: string
  word: string
  category: string
}

export interface RecategorizeGroupResponse {
  key_word_id: string
  key_anchor: string | null
  results: RecategorizeWordResult[]
}

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * Create a Word with sensible defaults.
 *
 * Applies defaults:
 * - status: 'collected' (word goes straight into the collection)
 * - discovery: user_brainstorm if not provided
 * - exploration: all false (nothing explored yet)
 * - timeline.discovered_at: current timestamp
 */
export function createWord(props: CreateWordProps): Word {
  const now = new Date()

  return {
    id: props.id,
    songId: props.songId,
    word: props.word,
    status: props.status ?? 'collected',
    discovery: props.discovery ?? { method: 'user_brainstorm' },
    notes: props.notes,
    exploration: {
      rhymes_explored: false,
      thesaurus_explored: false
    },
    timeline: {
      discovered_at: now
    }
  }
}
